import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'

export interface DeleteDependentDialogProps {
  /**
   * Full name of the dependent to delete.
   */
  fullName: string
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Callback when the dialog should close.
   */
  onClose?: () => unknown
  /**
   * Callback when the confirm delete button is pressed.
   */
  onConfirmDeletePress?: () => unknown
  /**
   * Whether the delete operation is pending.
   */
  isDeletePending?: boolean
  /**
   * Test ID attribute.
   */
  testId?: string
  /**
   * Test ID attribute for the confirm delete button.
   */
  confirmDeleteTestId?: string
  /**
   * Test ID attribute for the cancel button.
   */
  cancelTestId?: string
}

/**
 * A dialog for confirming the deletion of the dependent.
 */
export function DeleteDependentDialog({
  isOpen,
  onClose,
  fullName,
  isDeletePending,
  onConfirmDeletePress,
  testId,
  confirmDeleteTestId,
  cancelTestId,
}: DeleteDependentDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isDeletePending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>Permanently delete dependent?</ModalDialogTitle>
        <ModalDialogDescription>
          <p>
            Are you sure you want to permanently delete{' '}
            <span className="text-strong">{fullName}</span> as a dependent?
          </p>
          <p>
            Please note that by deleting, they will still remain on any prior
            job where they have been provided as a dependent, but they won't
            show up in the list of existing dependents on your account.
          </p>
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Yes, delete permanently"
            variant="default"
            onPress={onConfirmDeletePress}
            isPending={isDeletePending}
            testId={confirmDeleteTestId}
          />
          <Button
            label="No, cancel"
            variant="outline"
            onPress={onClose}
            isDisabled={isDeletePending}
            testId={cancelTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
